import { FACE_RANKS, RANK_VALUE } from './types';
// Wildcard helpers. A '*' rank matches any rank; a '*' suit matches any suit.
// At least one concrete rank/suit must be present to "anchor" most patterns —
// e.g. ['*', '*'] alone shouldn't trigger every Double, but ['*', '7'] does.
function rankMatches(a, b) {
    return a === b || a === '*' || b === '*';
}
function isFaceLike(r) {
    return r === '*' || FACE_RANKS.has(r);
}
function rankValue(r) {
    return r === '*' ? null : RANK_VALUE[r];
}
function hasAnyConcrete(cards, indices) {
    return indices.some((i) => cards[i].rank !== '*');
}
// Detect every pattern that fires on the given Plank (cards in play order).
// Returns a list of matches; multiple patterns can trigger from the same play.
//
// Detection is greedy/local, not exclusive — by design, K-Q-K fires both
// Sandwich (K-Q-K) and Marriage (K-Q adjacent at indices 0,1).
// That's the combo space we want.
export function detectPatterns(plank) {
    const matches = [];
    if (plank.length === 0)
        return matches;
    matches.push(...detectMutiny(plank));
    matches.push(...detectTriples(plank));
    matches.push(...detectDoubles(plank));
    matches.push(...detectSandwiches(plank));
    matches.push(...detectMarriages(plank));
    matches.push(...detectRuns(plank));
    matches.push(...detectSuitChains(plank));
    matches.push(...detectFlush(plank));
    matches.push(...detectStraightFlush(plank, matches));
    matches.push(...detectParitySquad(plank));
    matches.push(...detectRoyalty(plank));
    return matches;
}
// 3 same rank adjacent. Skipped if those positions are already inside a
// 4-of-a-kind Mutiny (which fires the bigger reward).
function detectTriples(plank) {
    const out = [];
    if (plank.length < 3)
        return out;
    const mutinyIndices = new Set();
    for (const m of detectMutiny(plank)) {
        for (const i of m.cardIndices)
            mutinyIndices.add(i);
    }
    for (let i = 0; i < plank.length - 2; i++) {
        const a = plank[i].rank, b = plank[i + 1].rank, c = plank[i + 2].rank;
        if (mutinyIndices.has(i) && mutinyIndices.has(i + 1) && mutinyIndices.has(i + 2))
            continue;
        if (rankMatches(a, b) && rankMatches(b, c) && hasAnyConcrete(plank, [i, i + 1, i + 2])) {
            const label = [a, b, c].find((r) => r !== '*') ?? '?';
            out.push({
                type: 'triple',
                cardIndices: [i, i + 1, i + 2],
                baseEarn: 4,
                baseSteal: 0,
                label: `Triple ${label}s`,
            });
        }
    }
    return out;
}
// 5+ cards of the same concrete suit, regardless of order. Wilds count as
// that suit. Only the longest single-suit flush per plank is reported.
function detectFlush(plank) {
    if (plank.length < 5)
        return [];
    const bySuit = new Map();
    const wildIndices = [];
    plank.forEach((c, i) => {
        if (c.suit === '*')
            wildIndices.push(i);
        else {
            const arr = bySuit.get(c.suit) ?? [];
            arr.push(i);
            bySuit.set(c.suit, arr);
        }
    });
    const out = [];
    for (const [suit, indices] of bySuit) {
        const total = indices.length + wildIndices.length;
        if (total >= 5) {
            const slots = [...indices];
            for (const w of wildIndices) {
                if (slots.length >= 5)
                    break;
                slots.push(w);
            }
            slots.sort((a, b) => a - b);
            const wildSuffix = indices.length < 5 ? ' (wild-aided)' : '';
            out.push({
                type: 'flush',
                cardIndices: slots,
                baseEarn: 0,
                baseSteal: 4,
                label: `${suit} flush${wildSuffix}`,
            });
            return out; // one flush per plank
        }
    }
    return out;
}
// A Run that is also entirely the same suit (or wild-suit). Bonus +3 earn /
// +3 steal layered on top of the existing Run + Suit Chain matches.
function detectStraightFlush(plank, baseMatches) {
    const out = [];
    for (const run of baseMatches) {
        if (run.type !== 'run')
            continue;
        const suits = run.cardIndices.map((i) => plank[i].suit);
        const concrete = suits.filter((s) => s !== '*');
        if (concrete.length === 0)
            continue;
        const set = new Set(concrete);
        if (set.size !== 1)
            continue;
        const suit = concrete[0];
        out.push({
            type: 'straight-flush',
            cardIndices: [...run.cardIndices],
            baseEarn: 3,
            baseSteal: 3,
            label: `Straight Flush (${suit})`,
        });
    }
    return out;
}
// 5+ all-even or all-odd ranks (excluding face cards). Aces are odd. Wilds
// fill either parity but require at least one concrete anchor.
function detectParitySquad(plank) {
    if (plank.length < 5)
        return [];
    const evenIdx = [];
    const oddIdx = [];
    const wildIdx = [];
    plank.forEach((c, i) => {
        const r = c.rank;
        if (r === '*') {
            wildIdx.push(i);
            return;
        }
        if (r === 'J' || r === 'Q' || r === 'K')
            return; // face cards excluded
        if (r === 'A') {
            oddIdx.push(i);
            return;
        } // Ace = 1 = odd
        const v = RANK_VALUE[r];
        (v % 2 === 0 ? evenIdx : oddIdx).push(i);
    });
    for (const [label, base] of [['even', evenIdx], ['odd', oddIdx]]) {
        if (base.length === 0)
            continue; // need a concrete anchor
        const total = base.length + wildIdx.length;
        if (total >= 5) {
            const slots = [...base];
            for (const w of wildIdx)
                if (!slots.includes(w))
                    slots.push(w);
            slots.sort((a, b) => a - b);
            return [{
                    type: 'parity-squad',
                    cardIndices: slots,
                    baseEarn: 3,
                    baseSteal: 1,
                    label: `${label.charAt(0).toUpperCase()}${label.slice(1)} Squad (${total})`,
                }];
        }
    }
    return [];
}
function detectDoubles(plank) {
    const out = [];
    for (let i = 0; i < plank.length - 1; i++) {
        const a = plank[i].rank, b = plank[i + 1].rank;
        if (rankMatches(a, b) && hasAnyConcrete(plank, [i, i + 1])) {
            const labelRank = a === '*' ? b : a;
            out.push({
                type: 'double',
                cardIndices: [i, i + 1],
                baseEarn: 2,
                baseSteal: 0,
                label: `Double ${labelRank}s`,
            });
        }
    }
    return out;
}
function detectSandwiches(plank) {
    const out = [];
    for (let i = 0; i < plank.length - 2; i++) {
        const a = plank[i].rank, mid = plank[i + 1].rank, c = plank[i + 2].rank;
        // Outer pair must "match" (with wild logic). Middle must be different
        // from the matched rank — but if outer is wild-wild, the matched rank
        // is unknown so accept any middle.
        if (!rankMatches(a, c))
            continue;
        if (!hasAnyConcrete(plank, [i, i + 2]))
            continue; // need outer anchor
        const matched = a === '*' ? c : a;
        if (mid !== '*' && mid === matched)
            continue; // bread can't equal filling
        out.push({
            type: 'sandwich',
            cardIndices: [i, i + 1, i + 2],
            baseEarn: 0,
            baseSteal: 3,
            label: `Sandwich (${a}-${mid}-${c})`,
        });
    }
    return out;
}
function detectMarriages(plank) {
    const out = [];
    for (let i = 0; i < plank.length - 1; i++) {
        const a = plank[i].rank, b = plank[i + 1].rank;
        // K-Q adjacent. A wild fills either side as long as the other is K or Q.
        const isKOrQ = (r) => r === 'K' || r === 'Q';
        let valid = false;
        if (a === 'K' && b === 'Q')
            valid = true;
        else if (a === 'Q' && b === 'K')
            valid = true;
        else if (a === '*' && isKOrQ(b))
            valid = true;
        else if (b === '*' && isKOrQ(a))
            valid = true;
        // Two wilds can't auto-marry — need at least one concrete K or Q.
        if (valid) {
            out.push({
                type: 'marriage',
                cardIndices: [i, i + 1],
                baseEarn: 5,
                baseSteal: 0,
                label: 'Marriage (K-Q)',
            });
        }
    }
    return out;
}
// Run = 3+ consecutive ranks ascending or descending. Wilds can fill any
// rank slot; we walk forward and check that any concrete card matches the
// expected offset from the first concrete anchor.
function detectRuns(plank) {
    const out = [];
    const consumed = new Set();
    for (let i = 0; i < plank.length; i++) {
        if (consumed.has(i))
            continue;
        const ascLen = runLengthFromIndex(plank, i, +1);
        const descLen = runLengthFromIndex(plank, i, -1);
        const len = Math.max(ascLen, descLen);
        if (len >= 3) {
            const indices = [];
            for (let k = 0; k < len; k++) {
                indices.push(i + k);
                consumed.add(i + k);
            }
            out.push({
                type: 'run',
                cardIndices: indices,
                baseEarn: len,
                baseSteal: 0,
                label: `Run of ${len}`,
            });
        }
    }
    return out;
}
// dir = +1 ascending, -1 descending. gapBudget allows N single-rank gaps
// (Helmsman = 1). Aces are flex: count as 1 (low) or 14 (high) — try both
// when the start is an Ace.
export function runLengthFromIndex(plank, start, dir, gapBudget = 0) {
    const startRank = plank[start].rank;
    if (startRank === 'A') {
        return Math.max(runLengthWithAnchor(plank, start, dir, 1, gapBudget), runLengthWithAnchor(plank, start, dir, 14, gapBudget));
    }
    if (startRank === '*') {
        return runLengthWithAnchor(plank, start, dir, null, gapBudget);
    }
    return runLengthWithAnchor(plank, start, dir, rankValue(startRank), gapBudget);
}
function runLengthWithAnchor(plank, start, dir, anchorVal, gapBudget = 0) {
    let length = 1;
    let lastVal = anchorVal;
    let gapsUsed = 0;
    let hasConcrete = anchorVal !== null;
    for (let j = start + 1; j < plank.length; j++) {
        const r = plank[j].rank;
        if (r === '*') {
            length += 1;
            if (lastVal !== null)
                lastVal = lastVal + dir;
            continue;
        }
        if (lastVal === null) {
            lastVal = rankValue(r);
            hasConcrete = true;
            length += 1;
            continue;
        }
        const v = rankValue(r);
        // Ace can be 14 (high) when next expected is 14
        if (r === 'A' && (lastVal + dir) === 14) {
            lastVal = 14;
            length += 1;
            continue;
        }
        if (v === lastVal + dir) {
            lastVal = v;
            length += 1;
            continue;
        }
        if (gapsUsed < gapBudget) {
            // Standard gap
            if (v === lastVal + 2 * dir) {
                lastVal = v;
                gapsUsed += 1;
                length += 1;
                continue;
            }
            // Ace high through a gap (e.g. J-Q-A)
            if (r === 'A' && (lastVal + 2 * dir) === 14) {
                lastVal = 14;
                gapsUsed += 1;
                length += 1;
                continue;
            }
        }
        break;
    }
    if (!hasConcrete)
        return 0;
    return length;
}
function detectSuitChains(plank) {
    const out = [];
    let i = 0;
    while (i < plank.length) {
        // Anchor suit = first concrete suit seen in this run; wilds match any.
        let anchorSuit = plank[i].suit === '*' ? null : plank[i].suit;
        let j = i + 1;
        while (j < plank.length) {
            const s = plank[j].suit;
            if (s === '*') {
                j++;
                continue;
            }
            if (anchorSuit === null) {
                anchorSuit = s;
                j++;
                continue;
            }
            if (s !== anchorSuit)
                break;
            j++;
        }
        const len = j - i;
        // Need at least one concrete suit in the chain to avoid all-wild fires.
        if (len >= 3 && anchorSuit !== null) {
            const indices = [];
            for (let k = i; k < j; k++)
                indices.push(k);
            out.push({
                type: 'suit-chain',
                cardIndices: indices,
                baseEarn: 0,
                baseSteal: 2,
                label: `${anchorSuit} chain of ${len}`,
            });
        }
        i = Math.max(j, i + 1);
    }
    return out;
}
function detectRoyalty(plank) {
    const out = [];
    for (let i = 0; i <= plank.length - 3; i++) {
        if (isFaceLike(plank[i].rank) && isFaceLike(plank[i + 1].rank) && isFaceLike(plank[i + 2].rank)
            && hasAnyConcrete(plank, [i, i + 1, i + 2])) {
            out.push({
                type: 'royalty',
                cardIndices: [i, i + 1, i + 2],
                baseEarn: 0,
                baseSteal: 0,
                forceDiscard: 2,
                label: 'Royalty Stack',
            });
        }
    }
    return out;
}
function detectMutiny(plank) {
    const out = [];
    const wildIndices = [];
    const byRank = new Map();
    plank.forEach((card, idx) => {
        if (card.rank === '*') {
            wildIndices.push(idx);
        }
        else {
            const arr = byRank.get(card.rank) ?? [];
            arr.push(idx);
            byRank.set(card.rank, arr);
        }
    });
    for (const [rank, indices] of byRank) {
        const total = indices.length + wildIndices.length;
        if (total >= 4) {
            const slots = indices.slice(0, 4);
            let w = 0;
            while (slots.length < 4 && w < wildIndices.length) {
                slots.push(wildIndices[w++]);
            }
            const wildSuffix = indices.length < 4 ? ' (wild-aided)' : '';
            out.push({
                type: 'mutiny',
                cardIndices: slots,
                baseEarn: 0,
                baseSteal: 8,
                label: `Mutiny (four ${rank}s)${wildSuffix}`,
            });
        }
    }
    return out;
}
